import type { JSX } from "react";

import { cn } from "@/shared/lib/cn";

interface ActivateToggleProps {
  /** Whether the promotion is currently active. */
  on: boolean;
  /** Finished promotions can't be toggled (locked, dimmed). */
  locked?: boolean;
  disabled?: boolean;
  label: string;
  onToggle: () => void;
}

/**
 * Per-row activate switch (mockup "Activate" column). Flips draft↔active via the
 * transition mutation upstream. Finished promotions render locked. Activation is
 * a state change only — firing waits for Phase 2 (see the Phase-2 banner).
 */
export function ActivateToggle({
  on,
  locked = false,
  disabled = false,
  label,
  onToggle,
}: ActivateToggleProps): JSX.Element {
  const isDisabled = locked || disabled;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={label}
      data-testid="promotion-activate-toggle"
      data-on={on}
      data-locked={locked}
      disabled={isDisabled}
      onClick={onToggle}
      className={cn(
        "relative h-[22px] w-[38px] shrink-0 rounded-full transition-colors",
        "after:absolute after:left-0.5 after:top-0.5 after:h-[18px] after:w-[18px] after:rounded-full after:bg-white after:shadow-[var(--sh-xs)] after:transition-transform",
        on
          ? "bg-[var(--intent-success-fg)] after:translate-x-4"
          : "bg-[var(--premium-border-strong)]",
        locked && "cursor-not-allowed bg-[var(--surface-sunken)] opacity-60",
        !isDisabled && "cursor-pointer",
      )}
    />
  );
}
